import {
  UI_LANGUAGES,
  parseSupportedUiLangCode,
  type UiLanguageCode,
} from '@/lib/ui-languages';

/** Anglais en premier : seule langue obligatoire, et repli des langues vides. */
export const HELP_CONTENT_LANGUAGES = [
  UI_LANGUAGES.find((l) => l.code === 'en')!,
  ...UI_LANGUAGES.filter((l) => l.code !== 'en'),
];

export type HelpTextMap = Partial<Record<UiLanguageCode, string>>;

export function blankHelpTextMap(): Record<UiLanguageCode, string> {
  return Object.fromEntries(UI_LANGUAGES.map((l) => [l.code, ''])) as Record<UiLanguageCode, string>;
}

export function asHelpTextMap(value: unknown): HelpTextMap {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: HelpTextMap = {};
  for (const lang of UI_LANGUAGES) {
    const raw = (value as Record<string, unknown>)[lang.code];
    if (typeof raw === 'string' && raw.trim()) out[lang.code] = raw.trim();
  }
  return out;
}

function mapHasText(map: HelpTextMap): boolean {
  return Object.values(map).some((v) => typeof v === 'string' && v.trim().length > 0);
}

/** Langue demandée, sinon anglais, sinon le texte déjà enregistré avant les traductions. */
export function resolveHelpText(
  map: unknown,
  lang: string | undefined,
  legacy: string,
): string {
  const stored = asHelpTextMap(map);
  const code = parseSupportedUiLangCode(lang) ?? 'en';
  const direct = (stored[code] ?? '').trim();
  if (direct) return direct;
  const en = (stored.en ?? '').trim();
  if (en) return en;
  return legacy.trim();
}

export function mapsFromStoredTopic(topic: {
  title: string;
  description: string;
  titleI18n?: unknown;
  descriptionI18n?: unknown;
}): { titles: Record<UiLanguageCode, string>; descriptions: Record<UiLanguageCode, string> } {
  const titles = blankHelpTextMap();
  const descriptions = blankHelpTextMap();
  const storedTitles = asHelpTextMap(topic.titleI18n);
  const storedDescriptions = asHelpTextMap(topic.descriptionI18n);
  if (!mapHasText(storedTitles) && !mapHasText(storedDescriptions)) {
    titles.fr = topic.title ?? '';
    descriptions.fr = topic.description ?? '';
    return { titles, descriptions };
  }
  for (const lang of UI_LANGUAGES) {
    titles[lang.code] = storedTitles[lang.code] ?? '';
    descriptions[lang.code] = storedDescriptions[lang.code] ?? '';
  }
  return { titles, descriptions };
}

export function compactHelpTextMap(map: Record<string, string>): HelpTextMap {
  const out: HelpTextMap = {};
  for (const lang of UI_LANGUAGES) {
    const value = (map[lang.code] ?? '').trim();
    if (value) out[lang.code] = value;
  }
  return out;
}
